import React from "react";
import { NavBar } from "../../common/NavBar";
import { ActionLogsTable } from "./ActionLogsTable";
import { ScheduleActionLogsTable } from "./ScheduleActionLogsTable";
import BackdropLoadingScreen from "../../common/BackdropLoadingScreen";

import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Link from "@mui/material/Link";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import { Breadcrumbs } from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import {
  getNumbersAfterActionLogs,
  handleFetchActionLogsData,
} from "./ActionLogsUtils/handleFetchActionLogsData";
import { handleFetchScheduleHistoryData } from "./ActionLogsUtils/handleFetchScheduleHistoryData";
import {
  handleFetchMuteHistoryData,
  MuteHistoryEvent,
} from "./ActionLogsUtils/handleFetchMuteHistoryData";
import { MuteHistoryTable } from "./MuteHistoryTable";
import { useFeatureFlag } from "../../../hooks/useFeatureFlag";

const tabsTheme = createTheme({
  palette: {
    primary: {
      main: "#FF8400",
    },
  },
});

export const ActionLogs = ({
  navBarCollapsed,
  setNavBarCollapsed,
}: {
  navBarCollapsed: any;
  setNavBarCollapsed: any;
}) => {
  const [auditLogsData, setActionLogsData] = React.useState([]);

  const [fetchingActionLogsData, setFetchingActionLogsData] =
    React.useState(true);

  const [page, setPage] = React.useState(0);

  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  const [paginationNumber, setPaginationNumber] = React.useState(1);

  const [loadingMoreActionLogsData, setLoadingMoreActionLogsData] =
    React.useState(false);

  const [siteName, setSiteName] = React.useState("");
  const [siteTimezone, setSiteTimezone] = React.useState<string | null>(null);

  const [clickedTab, setClickedTab] = React.useState<
    "general" | "schedule" | "mute"
  >("general");
  const [scheduleHistoryData, setScheduleHistoryData] = React.useState<any[]>(
    []
  );
  const [fetchingScheduleHistoryData, setFetchingScheduleHistoryData] =
    React.useState(true);
  // PROD-27: mute/unmute history from monitoring-api's durable audit rows.
  const [muteHistoryData, setMuteHistoryData] = React.useState<
    MuteHistoryEvent[]
  >([]);
  const [muteCameraNames, setMuteCameraNames] = React.useState<
    Map<string, string>
  >(new Map());
  const [fetchingMuteHistoryData, setFetchingMuteHistoryData] =
    React.useState(true);
  const [muteHistoryUnavailable, setMuteHistoryUnavailable] =
    React.useState(false);

  const navigate = useNavigate();
  const { isFeatureEnabled } = useFeatureFlag();
  const showScheduleActionLogs = isFeatureEnabled(
    "showScheduleActionLogs",
    false
  );

  React.useEffect(() => {
    const siteId = getNumbersAfterActionLogs(location.href);
    handleFetchActionLogsData(
      setActionLogsData,
      setFetchingActionLogsData,
      setSiteName,
      navigate,
      setSiteTimezone
    );
    if (showScheduleActionLogs && siteId) {
      handleFetchScheduleHistoryData(
        siteId,
        setScheduleHistoryData,
        setFetchingScheduleHistoryData,
        navigate
      );
    } else {
      setFetchingScheduleHistoryData(false);
    }
    // Mute history is for everyone who can mute — no flag (PROD-27).
    if (siteId) {
      handleFetchMuteHistoryData(
        siteId,
        setMuteHistoryData,
        setMuteCameraNames,
        setFetchingMuteHistoryData,
        setMuteHistoryUnavailable
      );
    } else {
      setFetchingMuteHistoryData(false);
    }
  }, [navigate, showScheduleActionLogs]);

  return (
    <div>
      <div className="bg-actuate-grey absolute w-screen h-screen">
        <BackdropLoadingScreen
          openState={
            fetchingActionLogsData ||
            (showScheduleActionLogs && fetchingScheduleHistoryData) ||
            fetchingMuteHistoryData
          }
        />
        <BackdropLoadingScreen openState={loadingMoreActionLogsData} />
        <div
          className={
            !navBarCollapsed
              ? "absolute top-[20px] left-[150px] right-[1px] h-[19px]] bg-white"
              : "absolute top-[20px] left-[75px] right-[1px] h-[19px]] bg-white"
          }
        >
          <Stack spacing={2}>
            <Breadcrumbs
              separator={<NavigateNextIcon fontSize="medium" />}
              aria-label="breadcrumb"
              sx={{
                paddingLeft: "20px",
              }}
            >
              {siteName && (
                <Link
                  underline="hover"
                  color="inherit"
                  href={`/sites/aboutsite/${getNumbersAfterActionLogs(
                    location.href
                  )}`}
                  sx={{
                    fontSize: "20px",
                    fontFamily: "Mulish",
                    fontWeight: "bold",
                  }}
                  variant="h5"
                >
                  {siteName}
                </Link>
              )}
              <p className="text-black font-bold font-mulish text-[20px] pt-3 pb-3">
                Action Logs
              </p>
            </Breadcrumbs>
          </Stack>
        </div>
        <div
          className={
            !navBarCollapsed
              ? "absolute left-[150px] top-[95px] right-[1px]"
              : "absolute left-[75px] top-[95px] right-[1px]"
          }
        >
          <Box
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              marginBottom: 0,
              backgroundColor: "white",
            }}
          >
            <ThemeProvider theme={tabsTheme}>
              <Tabs
                value={clickedTab}
                onChange={(_event, tabValue: "general" | "schedule" | "mute") =>
                  setClickedTab(tabValue)
                }
                aria-label="action logs tabs"
                color="primary"
              >
                <Tab
                  value="general"
                  label="General Action Logs"
                  sx={{
                    fontFamily: "Mulish",
                    fontWeight: "Bold",
                    color: "primary",
                  }}
                />
                {showScheduleActionLogs && (
                  <Tab
                    value="schedule"
                    label="Schedule Action Logs"
                    sx={{
                      fontFamily: "Mulish",
                      fontWeight: "Bold",
                      color: "primary",
                    }}
                  />
                )}
                <Tab
                  value="mute"
                  label="Mute History"
                  sx={{
                    fontFamily: "Mulish",
                    fontWeight: "Bold",
                    color: "primary",
                  }}
                />
              </Tabs>
            </ThemeProvider>
          </Box>
          {clickedTab === "general" && !fetchingActionLogsData && (
            <ActionLogsTable
              auditLogsData={auditLogsData}
              page={page}
              setPage={setPage}
              rowsPerPage={rowsPerPage}
              setRowsPerPage={setRowsPerPage}
              paginationNumber={paginationNumber}
              setLoadingMoreActionLogsData={setLoadingMoreActionLogsData}
              setActionLogsData={setActionLogsData}
              setPaginationNumber={setPaginationNumber}
              navigate={navigate}
              siteTimezone={siteTimezone}
            />
          )}
          {clickedTab === "schedule" && (
            <ScheduleActionLogsTable
              scheduleHistoryResults={scheduleHistoryData}
              siteTimezone={siteTimezone}
            />
          )}
          {clickedTab === "mute" && (
            <MuteHistoryTable
              muteHistoryResults={muteHistoryData}
              cameraNames={muteCameraNames}
              unavailable={muteHistoryUnavailable}
              siteTimezone={siteTimezone}
            />
          )}
        </div>
      </div>
      <div className="justify-start">
        <NavBar
          navBarCollapsed={navBarCollapsed}
          setNavBarCollapsed={setNavBarCollapsed}
          props={{}}
        />
      </div>
    </div>
  );
};
